import { Activity, Gauge, Flame, HeartPulse, Scale, Timer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
interface MetricItem {
  id: string;
  label: React.ReactNode;
  icon: typeof Activity;
}
const MEASURED_METRICS: MetricItem[] = [{
  id: "vo2_peak",
  label: <span>
        VO<sub>2</sub> Max or achieved peak oxygen uptake
      </span>,
  icon: Activity
}, {
  id: "exercise_response",
  label: "Exercise response",
  icon: HeartPulse
}, {
  id: "intensity_zones",
  label: "Personalised exercise intensity zones",
  icon: Gauge
}, {
  id: "respiratory_exchange",
  label: "Exercise respiratory exchange",
  icon: Timer
}, {
  id: "substrate_utilisation",
  label: "Relative fat/carbohydrate utilisation",
  icon: Scale
}, {
  id: "calorie_expenditure",
  label: "Exercise calorie expenditure",
  icon: Flame
}];
export default function CardiorespiratoryFitness() {
  return <section id="cardiorespiratoryfitnessinfo" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground leading-tight">
            Cardiorespiratory fitness: one of the most informative measures of
            physical fitness
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Cardiorespiratory fitness describes the ability of the heart, lungs,
            circulation and muscles to work together during exercise. VO<sub>2</sub> Max
            provides an objective measure of this capacity.
          </p>

          <Alert className="mt-8 border-border/80 bg-secondary/40">
            <AlertDescription className="text-sm text-foreground/80 leading-relaxed">
              Higher levels of cardiorespiratory fitness are associated in
              population research with better health outcomes and lower risk of
              premature mortality. An individual&apos;s VO<sub>2</sub> Max
              result, however, should not be interpreted as a prediction of
              lifespan.
            </AlertDescription>
          </Alert>
        </div>

        <div className="mt-14 pt-10 border-t border-border">
          <p className="text-base sm:text-lg font-medium text-foreground">
            PhysAnalytica can use the KORR CardioCoach MAX to measure:
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {MEASURED_METRICS.map((metric, i) => {
            const Icon = metric.icon;
            return <div key={metric.id} data-index={i}>
                  <Card className="h-full border border-border shadow-xs hover:border-border/80 transition-colors">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="size-5" aria-hidden="true" />
                      </div>
                      <div className="text-sm sm:text-base font-medium text-foreground pt-1 leading-snug">
                        {metric.label}
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-12 flex items-center">
          <Button asChild size="lg" className="rounded-md">
            <Link to="/BookNowPage">
              Measure Your VO<sub>2</sub> Max
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}